import React, { useState, useEffect } from "react";
import { Box, Tabs, Tab, TextField, Button, Typography, CircularProgress, MenuItem, Select, InputLabel, FormControl, Card } from "@mui/material";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc, getDoc, getDocs, collection } from "firebase/firestore";
import { db, storage } from "./firebaseServices";
import SnackbarMessage from "./SnackbarMessage";

function AddEditCategory() {
  const [tabValue, setTabValue] = useState(0); // Track selected tab
  const [catName, setCatName] = useState("");
  const [catImg, setCatImg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // State for editing
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [editImgPreview, setEditImgPreview] = useState("");

  // Handle tab switching
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    clearForm();
  };

  // Fetch categories for the dropdown
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "categories"));
        const categories = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCategories(categories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  // Clear form inputs
  const clearForm = () => {
    setCatName("");
    setCatImg(null);
    setSelectedCategory("");
    setEditImgPreview("");
    setMessage("");
  };

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setCatImg(e.target.files[0]);
    }
  };

  // Handle add category
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!catName || !catImg) {
      setMessage("Please provide both a category name and an image.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const storageRef = ref(storage, `categories/${catImg.name}`);
      await uploadBytes(storageRef, catImg);
      const imageUrl = await getDownloadURL(storageRef);

      const categoryDoc = doc(db, "categories", catName);
      await setDoc(categoryDoc, {
        catName,
        catImg: imageUrl,
      });

      setMessage(`Category "${catName}" added successfully!`);
      clearForm();
    } catch (error) {
      console.error("Error adding category:", error);
      setMessage("Failed to add category. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle category selection for editing
  const handleCategorySelect = async (e) => {
    const selectedId = e.target.value;
    setSelectedCategory(selectedId);

    if (selectedId) {
      try {
        const categoryDoc = await getDoc(doc(db, "categories", selectedId));
        if (categoryDoc.exists()) {
          const data = categoryDoc.data();
          setCatName(data.catName);
          setEditImgPreview(data.catImg); // Show existing image
        }
      } catch (error) {
        console.error("Error loading category data:", error);
      }
    }
  };

  // Handle edit category
  const handleEditCategory = async (e) => {
    e.preventDefault();
    if (!selectedCategory) {
      setMessage("Please select a category to edit.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      let imageUrl = editImgPreview; // Keep existing image if no new file is uploaded

      if (catImg) {
        const storageRef = ref(storage, `categories/${catImg.name}`);
        await uploadBytes(storageRef, catImg);
        imageUrl = await getDownloadURL(storageRef);
      }

      const categoryDoc = doc(db, "categories", selectedCategory);
      await setDoc(categoryDoc, {
        catName,
        catImg: imageUrl,
      });

      setMessage(`Category "${catName}" updated successfully!`);
      clearForm();
    } catch (error) {
      console.error("Error updating category:", error);
      setMessage("Failed to update category. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Card
        sx={{
          backgroundColor: "white",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)", // Drop shadow
          borderRadius: "8px", // Rounded corners
          padding: 3, // Internal padding
          maxWidth: "800px", // Limit max width
          margin: "auto", // Center horizontally
          mt: 4, // Add some margin from the top
        }}
      >
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Add Category" />
          <Tab label="Edit Categories" />
        </Tabs>
        {tabValue === 0 && (
          <Box component="form" onSubmit={handleAddCategory} sx={{ mt: 3 }}>
            <TextField label="Category Name" variant="outlined" fullWidth value={catName} onChange={(e) => setCatName(e.target.value)} required />

            <Box
              sx={{
                display: "flex", // Use Flexbox
                justifyContent: "space-between", // Space buttons on opposite sides
                alignItems: "center", // Align buttons vertically
                mt: 2, // Add top margin
              }}
            >
              <Button variant="contained" component="label" sx={{ mt: 2 }}>
                Upload Image
                <input type="file" hidden accept="image/*" onChange={handleFileChange} />
              </Button>
              {catImg && <Typography>{catImg.name}</Typography>}
              <Button type="submit" variant="contained" color="primary" sx={{ mt: 2 }} disabled={loading || !catName || !catImg}>
                {loading ? <CircularProgress size={24} /> : "Add Category"}
              </Button>
              <SnackbarMessage message={message} setMessage={setMessage} /> {/* Include Snackbar */}
            </Box>
          </Box>
        )}
        {tabValue === 1 && (
          <Box component="form" onSubmit={handleEditCategory} sx={{ mt: 3 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Category</InputLabel>
              <Select value={selectedCategory} onChange={handleCategorySelect} required>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.catName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Category Name" variant="outlined" fullWidth value={catName} onChange={(e) => setCatName(e.target.value)} required />
            {editImgPreview && (
              <Box sx={{ mt: 2 }}>
                <Typography>Existing Image:</Typography>
                <img src={editImgPreview} alt="Category" style={{ maxWidth: "100%", maxHeight: "150px" }} />
              </Box>
            )}
            <Box
              sx={{
                display: "flex", // Use Flexbox
                justifyContent: "space-between", // Space buttons on opposite sides
                alignItems: "center", // Align buttons vertically
                mt: 2, // Add top margin
              }}
            >
              <Button variant="contained" component="label" sx={{ mt: 2 }}>
                Upload New Image
                <input type="file" hidden accept="image/*" onChange={handleFileChange} />
              </Button>
              {catImg && <Typography>{catImg.name}</Typography>}
              <Button type="submit" variant="contained" color="primary" sx={{ mt: 2 }} disabled={loading || !catName || !catImg}>
                {loading ? <CircularProgress size={24} /> : "Update Category"}
              </Button>
            </Box>
          </Box>
        )}
        <SnackbarMessage message={message} setMessage={setMessage} /> {/* Include Snackbar */}
      </Card>
    </Box>
  );
}

export default AddEditCategory;
